import { Injectable, computed, signal } from '@angular/core';
import { StorefrontTenant } from '../models/storefront.model';

const MARKET_KEY = 'coreshop_storefront_market';

/** Contexto de la tienda pública resuelto desde `/store/:storeSlug`. */
@Injectable({ providedIn: 'root' })
export class StorefrontContextService {
  readonly companyId = signal(0);
  readonly storeSlug = signal('');
  readonly storeName = signal('');
  readonly logoUrl = signal<string | null>(null);
  /** Código de mercado activo (?market= o sesión). */
  readonly marketCode = signal<string | null>(null);
  readonly marketName = signal<string | null>(null);
  readonly marketSandbox = signal(false);
  /** Nombre del producto que se está viendo actualmente (página de detalle), o null fuera de esa página. */
  readonly currentProductName = signal<string | null>(null);

  readonly isSandboxPreview = computed(() => this.marketSandbox());

  /** Nombre visible de la tienda (o su slug mientras no hay nombre). */
  readonly displayName = computed(() => this.storeName().trim() || this.storeSlug());

  /** #FUNCIONALIDAD Iniciales de la tienda (máx. 2) para el monograma de marca, favicon e imágenes faltantes. */
  readonly storeInitials = computed(() => {
    const words = this.displayName().split(/[\s\-_]+/).filter(Boolean);
    const letters = words.length > 1 ? words[0][0] + words[1][0] : (words[0] ?? '').slice(0, 2);
    return letters.toUpperCase();
  });

  setCurrentProduct(name: string | null): void {
    this.currentProductName.set(name?.trim() || null);
  }

  setTenant(tenant: StorefrontTenant): void {
    this.companyId.set(tenant.companyId);
    this.storeSlug.set(tenant.storeSlug);
    this.storeName.set(tenant.storeName);
    this.logoUrl.set(tenant.logoUrl?.trim() || null);
    this.restoreMarketFromStorage(tenant.companyId);
  }

  setMarket(companyId: number, code: string | null, name?: string | null, sandbox = false): void {
    const normalized = code?.trim() || null;
    this.marketCode.set(normalized);
    this.marketName.set(name ?? null);
    this.marketSandbox.set(!!sandbox && !!normalized);
    if (companyId > 0) {
      if (normalized) {
        sessionStorage.setItem(
          `${MARKET_KEY}_${companyId}`,
          JSON.stringify({ code: normalized, name: name ?? null, sandbox: !!sandbox }),
        );
      } else {
        sessionStorage.removeItem(`${MARKET_KEY}_${companyId}`);
      }
    }
  }

  clearMarket(companyId: number): void {
    this.setMarket(companyId, null);
  }

  private restoreMarketFromStorage(companyId: number): void {
    if (this.marketCode()) return;
    const raw = sessionStorage.getItem(`${MARKET_KEY}_${companyId}`);
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw) as { code?: string; name?: string | null; sandbox?: boolean };
      if (parsed.code) {
        this.marketCode.set(parsed.code);
        this.marketName.set(parsed.name ?? null);
        this.marketSandbox.set(!!parsed.sandbox);
      }
    } catch {
      /* ignore */
    }
  }

  /** Segmentos para `routerLink`, p. ej. `storeLink('cart')` → `['/store', 'lesly-sac', 'cart']`. */
  storeLink(...segments: string[]): string[] {
    const slug = this.storeSlug();
    if (!slug) return ['/'];
    const parts = segments.filter(s => s.length > 0);
    return parts.length ? ['/store', slug, ...parts] : ['/store', slug];
  }
}
